import React from 'react';

import { CustomPlotStylingOptions } from '@components/analysisCategories/comparative/plots/PlotlyVolcanoPlotUtil';
import PlotlyPieChart from '@components/analysisCategories/genome/plots/pie/PlotlyPieChart';
import PlotNotSupportedView from '@components/analysisCategories/PlotNotSupportedView';
import BarPlotView from '@components/analysisCategories/summary/plots/BarPlotView';
import BoxPlotView from '@components/analysisCategories/summary/plots/BoxPlotView';
import HeatmapView from '@components/analysisCategories/summary/plots/HeatmapView';
import LinePlotView from '@components/analysisCategories/summary/plots/LinePlotView';
import PlotlyCellScatterPlot from '@components/analysisCategories/summary/plots/PlotlyCellScatterPlot';
import RidgePlotView from '@components/analysisCategories/summary/plots/RidgePlotView';
import ViolinPlotView from '@components/analysisCategories/summary/plots/ViolinPlotView';
import PlotLoadingView from '@components/plots/PlotLoadingView';
import PlotNoDataView from '@components/plots/PlotNoDataView';

import { StateSetter } from '@contexts/ContextTypes';
import { usePlotContext } from '@contexts/PlotContext';

import { SummaryAnalysisPlotData } from '@models/ExperimentData';

import PlotlyStackedBarPlot from '../genome/plots/stackedBars/PlotlyStackedBarPlot';

type Props = {
    customPlotStylingOptions: CustomPlotStylingOptions | null;
    isPlotFullyRendered?: boolean;
    setIsPlotFullyRendered?: StateSetter<boolean>;
};

const SummaryPlotDisplayView = ({ customPlotStylingOptions, isPlotFullyRendered, setIsPlotFullyRendered }: Props) => {
    const { plot, plotData, experiment } = usePlotContext();
    const data = plotData as SummaryAnalysisPlotData | null | undefined;

    if (data?.pipeline_status === 'in_progress' || !plot) {
        return <PlotLoadingView />;
    }

    if (!data || (data.count ?? 0) === 0) {
        return <PlotNoDataView />;
    }
    switch (plot.display.display_type) {
        case 'box_plot':
            return (
                <BoxPlotView plot={plot} experiment={experiment} customPlotStylingOptions={customPlotStylingOptions} />
            );
        case 'bar_plot':
            return (
                <BarPlotView plot={plot} experiment={experiment} customPlotStylingOptions={customPlotStylingOptions} />
            );
        case 'stacked_bar_plot':
            return <PlotlyStackedBarPlot experiment={experiment} customPlotStylingOptions={customPlotStylingOptions} />;
        case 'heatmap':
            return <HeatmapView plot={plot} experiment={experiment} />;
        case 'line_plot':
            return <LinePlotView customPlotStylingOptions={customPlotStylingOptions} />;
        case 'ridge_plot':
            return <RidgePlotView customPlotStylingOptions={customPlotStylingOptions} />;
        case 'violin_plot':
            return <ViolinPlotView customPlotStylingOptions={customPlotStylingOptions} />;
        case 'cell_scatter_plot':
            return (
                <PlotlyCellScatterPlot
                    customPlotStylingOptions={customPlotStylingOptions}
                    isPlotFullyRendered={isPlotFullyRendered}
                    setIsPlotFullyRendered={setIsPlotFullyRendered}
                />
            );
        case 'pie_chart':
            return <PlotlyPieChart experiment={experiment} />;
        default:
            return <PlotNotSupportedView />;
    }
};

export default SummaryPlotDisplayView;
